import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  Box,
  Typography,
  CircularProgress,
  IconButton,
  Alert,
  useTheme,
  alpha,
} from '@mui/material';
import {
  Close as CloseIcon,
  Download as DownloadIcon,
} from '@mui/icons-material';
import {
  PDFViewer,
  PDFViewerRef,
  ExportPlugin,
  AnnotationPlugin,
  AnnotationEvent,
} from '@embedpdf/react-pdf-viewer';
import { useAuth } from '../contexts/AuthContext';
import { triggerBlobDownload } from '../utils/filePreview';

/** Hide editing mode tabs; keep view/navigation/zoom only. */
const READ_ONLY_DISABLED_CATEGORIES = [
  'document-open',
  'document-export',
  'document-print',
  'annotation',
  'annotation-shape',
  'insert',
  'form',
  'redaction',
] as const;

const ANNOTATE_DISABLED_CATEGORIES = [
  'document-open',
  'document-export',
  'document-print',
] as const;

const AUTO_SAVE_DEBOUNCE_MS = 900;

export interface PdfViewerDialogProps {
  open: boolean;
  src: string | null;
  filename: string;
  onClose: () => void;
  title?: string;
  /** Enable EmbedPDF annotation tools. */
  enableAnnotations?: boolean;
  /**
   * Persist the annotated PDF (bytes include all annotations).
   * Called on Save and auto-saved after committed annotation changes.
   */
  onSaveAnnotatedPdf?: (blob: Blob) => Promise<void>;
}

const PdfViewerDialog: React.FC<PdfViewerDialogProps> = ({
  open,
  src,
  filename,
  onClose,
  title,
  enableAnnotations = false,
  onSaveAnnotatedPdf,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const { user } = useAuth();
  const annotationAuthor = user?.name?.trim() || user?.trigram?.trim() || 'User';
  const viewerRef = useRef<PDFViewerRef>(null);
  const onSaveRef = useRef(onSaveAnnotatedPdf);
  const autoSaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const savingRef = useRef(false);
  const pendingAutoSaveRef = useRef(false);
  const annotationUnsubRef = useRef<(() => void) | null>(null);

  const [viewerReady, setViewerReady] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saved' | 'error'>('idle');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    onSaveRef.current = onSaveAnnotatedPdf;
  }, [onSaveAnnotatedPdf]);

  useEffect(() => {
    if (!open) {
      setViewerReady(false);
      setDownloading(false);
      setSaving(false);
      setDirty(false);
      setSaveStatus('idle');
      setError(null);
      pendingAutoSaveRef.current = false;
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
        autoSaveTimerRef.current = null;
      }
      annotationUnsubRef.current?.();
      annotationUnsubRef.current = null;
    }
  }, [open]);

  useEffect(() => {
    if (!open || !viewerRef.current?.container) return;
    viewerRef.current.container.setTheme({ preference: isDark ? 'dark' : 'light' });
  }, [open, isDark]);

  const exportPdfBytes = useCallback(async (): Promise<ArrayBuffer> => {
    const registry = await viewerRef.current?.registry;
    if (!registry) {
      throw new Error('PDF viewer is not ready yet. Please wait a moment and try again.');
    }

    const exportPlugin = registry.getPlugin<ExportPlugin>('export')?.provides();
    if (!exportPlugin) {
      throw new Error('Export is unavailable in the PDF viewer.');
    }

    return exportPlugin.saveAsCopy().toPromise();
  }, []);

  const persistAnnotatedPdf = useCallback(async () => {
    const saveHandler = onSaveRef.current;
    if (!saveHandler) return;

    if (savingRef.current) {
      pendingAutoSaveRef.current = true;
      return;
    }

    savingRef.current = true;
    setSaving(true);
    setError(null);
    setSaveStatus('idle');
    try {
      const arrayBuffer = await exportPdfBytes();
      const blob = new Blob([arrayBuffer], { type: 'application/pdf' });
      await saveHandler(blob);
      setDirty(false);
      setSaveStatus('saved');
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to save annotated PDF';
      setError(message);
      setSaveStatus('error');
      throw err;
    } finally {
      savingRef.current = false;
      setSaving(false);
      if (pendingAutoSaveRef.current) {
        pendingAutoSaveRef.current = false;
        window.setTimeout(() => {
          void persistAnnotatedPdf().catch(() => {
            /* error state already set */
          });
        }, 0);
      }
    }
  }, [exportPdfBytes]);

  const scheduleAutoSave = useCallback(() => {
    if (!onSaveRef.current) return;
    setDirty(true);
    setSaveStatus('idle');
    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
    }
    autoSaveTimerRef.current = setTimeout(() => {
      autoSaveTimerRef.current = null;
      void persistAnnotatedPdf().catch(() => {
        /* error state already set */
      });
    }, AUTO_SAVE_DEBOUNCE_MS);
  }, [persistAnnotatedPdf]);

  const handleViewerReady = useCallback(
    (registry: Awaited<NonNullable<PDFViewerRef['registry']>>) => {
      setViewerReady(true);
      annotationUnsubRef.current?.();
      annotationUnsubRef.current = null;

      if (!enableAnnotations || !onSaveRef.current || !registry) return;

      const annotationApi = registry.getPlugin<AnnotationPlugin>('annotation')?.provides();
      if (!annotationApi?.onAnnotationEvent) return;

      annotationUnsubRef.current = annotationApi.onAnnotationEvent((event: AnnotationEvent) => {
        if (event.type === 'loaded') return;
        if (!('committed' in event) || !event.committed) return;
        scheduleAutoSave();
      });
    },
    [enableAnnotations, scheduleAutoSave]
  );

  useEffect(() => {
    return () => {
      annotationUnsubRef.current?.();
      annotationUnsubRef.current = null;
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
      }
    };
  }, []);

  const handleDownload = useCallback(async () => {
    setError(null);
    setDownloading(true);
    try {
      if (enableAnnotations && viewerReady) {
        const arrayBuffer = await exportPdfBytes();
        const blob = new Blob([arrayBuffer], { type: 'application/pdf' });
        triggerBlobDownload(blob, filename, 'application/pdf');
        return;
      }
      if (!src) return;
      const response = await fetch(src);
      const blob = await response.blob();
      triggerBlobDownload(blob, filename, 'application/pdf');
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to download PDF';
      setError(message);
    } finally {
      setDownloading(false);
    }
  }, [enableAnnotations, exportPdfBytes, filename, src, viewerReady]);

  const handleClose = useCallback(async () => {
    if (saving || downloading) return;

    if (enableAnnotations && onSaveRef.current && (dirty || autoSaveTimerRef.current)) {
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
        autoSaveTimerRef.current = null;
      }
      try {
        await persistAnnotatedPdf();
      } catch {
        return;
      }
    }
    onClose();
  }, [dirty, downloading, enableAnnotations, onClose, persistAnnotatedPdf, saving]);

  const consoleColors = {
    surface: isDark ? '#0f2134' : '#ffffff',
    border: isDark ? 'rgba(204, 186, 155, 0.15)' : 'rgba(15, 33, 52, 0.12)',
    accent: '#ccba9b',
    textPrimary: isDark ? '#ffffff' : '#0f2134',
    textSecondary: isDark ? '#b0bec5' : '#546e7a',
    borderStrong: isDark ? 'rgba(204, 186, 155, 0.3)' : 'rgba(15, 33, 52, 0.2)',
  };

  const busy = saving || downloading;

  return (
    <Dialog
      open={open}
      onClose={busy ? undefined : () => void handleClose()}
      fullScreen
      PaperProps={{
        sx: {
          bgcolor: consoleColors.surface,
          border: `1px solid ${consoleColors.border}`,
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          borderBottom: `1px solid ${consoleColors.border}`,
          bgcolor: isDark ? alpha('#0f2134', 0.5) : alpha('#0f2134', 0.03),
          py: 1.5,
        }}
      >
        <Typography
          variant="subtitle1"
          sx={{
            flex: 1,
            fontWeight: 600,
            color: consoleColors.textPrimary,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            minWidth: 0,
          }}
        >
          {title ?? filename}
        </Typography>
        {enableAnnotations && onSaveAnnotatedPdf && (
          <Typography
            variant="caption"
            sx={{
              color:
                saveStatus === 'error'
                  ? 'error.main'
                  : saveStatus === 'saved'
                    ? 'success.main'
                    : consoleColors.textSecondary,
              flexShrink: 0,
              minWidth: 72,
              textAlign: 'right',
            }}
          >
            {saving ? 'Saving…' : dirty ? 'Unsaved' : saveStatus === 'saved' ? 'Saved' : ''}
          </Typography>
        )}
        <Button
          size="small"
          variant="outlined"
          startIcon={
            downloading ? (
              <CircularProgress size={16} sx={{ color: consoleColors.accent }} />
            ) : (
              <DownloadIcon sx={{ fontSize: 16 }} />
            )
          }
          onClick={() => void handleDownload()}
          disabled={!src || !viewerReady || busy}
          sx={{
            color: consoleColors.textPrimary,
            borderColor: consoleColors.borderStrong,
            flexShrink: 0,
            '&:hover': {
              borderColor: consoleColors.accent,
              bgcolor: alpha(consoleColors.accent, 0.08),
            },
          }}
        >
          Download
        </Button>
        <IconButton
          aria-label="Close viewer"
          onClick={() => void handleClose()}
          disabled={busy}
          sx={{ color: consoleColors.textSecondary }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        {error && (
          <Alert severity="error" sx={{ m: 2, flexShrink: 0 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        <Box sx={{ flex: 1, minHeight: 0, position: 'relative' }}>
          {!src ? (
            <Box
              sx={{
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <CircularProgress sx={{ color: consoleColors.accent }} />
            </Box>
          ) : (
            <>
              {!viewerReady && (
                <Box
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: alpha(consoleColors.surface, 0.85),
                    zIndex: 1,
                  }}
                >
                  <CircularProgress sx={{ color: consoleColors.accent }} />
                </Box>
              )}
              <PDFViewer
                ref={viewerRef}
                config={{
                  src,
                  tabBar: 'never',
                  theme: { preference: isDark ? 'dark' : 'light' },
                  disabledCategories: enableAnnotations
                    ? [...ANNOTATE_DISABLED_CATEGORIES]
                    : [...READ_ONLY_DISABLED_CATEGORIES],
                  ...(enableAnnotations
                    ? {
                        export: { defaultFileName: filename },
                        annotations: { annotationAuthor },
                      }
                    : {}),
                }}
                style={{ width: '100%', height: '100%' }}
                onReady={(registry) => {
                  handleViewerReady(registry);
                }}
              />
            </>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default PdfViewerDialog;
